import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import WordWheelPointsTable from "./WordWheelPointsTable";

export default function WordWheelCompleteDialog({
    open,
    onClose,
    durationLabel,
    coinsEarned,
    hintCoinsSpent = 0,
    totalPuzzleCoins,
    wordCount,
    coinsRows = [],
    colors,
    /** Scope modal to a positioned parent (e.g. 9:16 recording phone). */
    contained = false,
}) {
    const surface = colors?.surface || "#fff";
    const text = colors?.textOnSurface || colors?.text || "#0f3d36";
    const showScoring = Array.isArray(coinsRows) && coinsRows.length > 0;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xs"
            disablePortal={contained}
            disableScrollLock={contained}
            hideBackdrop={false}
            slotProps={{
                backdrop: contained
                    ? {
                          sx: {
                              position: "absolute",
                          },
                      }
                    : undefined,
            }}
            sx={
                contained
                    ? {
                          position: "absolute",
                          inset: 0,
                          zIndex: 30,
                          "& .MuiDialog-container": {
                              position: "absolute",
                              inset: 0,
                              alignItems: "center",
                          },
                      }
                    : undefined
            }
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    bgcolor: surface,
                    backgroundImage: "none",
                    mx: 2,
                    ...(contained
                        ? {
                              maxHeight: "min(78%, 520px)",
                              m: 1.5,
                              width: "calc(100% - 24px)",
                          }
                        : {}),
                },
            }}
        >
            <DialogTitle
                sx={{
                    textAlign: "center",
                    fontWeight: 800,
                    color: "#059669",
                    pt: 2.5,
                    pb: 1,
                }}
            >
                Level complete!
            </DialogTitle>
            <DialogContent sx={{ px: 2.5, pb: 1, color: text }}>
                <Box
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: 1.5,
                        py: 0.5,
                        mb: 1.75,
                        mx: "auto",
                        width: "fit-content",
                        borderRadius: 999,
                        bgcolor: "rgba(5, 150, 105, 0.1)",
                        border: "1px solid rgba(5, 150, 105, 0.25)",
                    }}
                >
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#059669" }}>
                        Season Journey milestone
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 1.25,
                        mb: 2,
                    }}
                >
                    <Box
                        sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: "rgba(5, 150, 105, 0.08)",
                            border: "1px solid rgba(5, 150, 105, 0.18)",
                            textAlign: "center",
                        }}
                    >
                        <Typography variant="caption" sx={{ display: "block", color: "rgba(15, 61, 54, 0.65)", mb: 0.5 }}>
                            Time
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0f3d36", lineHeight: 1.25 }}>
                            {durationLabel || "—"}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: "rgba(5, 150, 105, 0.08)",
                            border: "1px solid rgba(5, 150, 105, 0.18)",
                            textAlign: "center",
                        }}
                    >
                        <Typography variant="caption" sx={{ display: "block", color: "rgba(15, 61, 54, 0.65)", mb: 0.5 }}>
                            Coins
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#059669", lineHeight: 1.25 }}>
                            {coinsEarned ?? 0}
                        </Typography>
                    </Box>
                </Box>

                {wordCount != null && (
                    <Typography variant="body2" sx={{ textAlign: "center", mb: 0.75, color: "rgba(15, 61, 54, 0.72)" }}>
                        {wordCount} word{wordCount === 1 ? "" : "s"} found
                    </Typography>
                )}

                {hintCoinsSpent > 0 && (
                    <Typography variant="caption" sx={{ display: "block", textAlign: "center", mb: 1, color: "rgba(15, 61, 54, 0.65)" }}>
                        Hints used: −{hintCoinsSpent} coins
                    </Typography>
                )}

                {totalPuzzleCoins != null && totalPuzzleCoins > 0 && (
                    <Typography variant="caption" sx={{ display: "block", textAlign: "center", mb: 1.5, color: "rgba(15, 61, 54, 0.65)" }}>
                        Lifetime total: {totalPuzzleCoins} coins
                    </Typography>
                )}

                {showScoring ? (
                    <>
                        <Typography
                            variant="overline"
                            sx={{
                                display: "block",
                                letterSpacing: 1.1,
                                fontWeight: 700,
                                color: "rgba(15, 61, 54, 0.55)",
                                mb: 0.75,
                            }}
                        >
                            Scoring
                        </Typography>
                        <WordWheelPointsTable rows={coinsRows} compact />
                    </>
                ) : null}
            </DialogContent>
            <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
                <Button
                    fullWidth
                    variant="contained"
                    onClick={onClose}
                    sx={{
                        minHeight: 44,
                        bgcolor: "#059669",
                        fontWeight: 700,
                        textTransform: "none",
                        "&:hover": { bgcolor: "#047857" },
                    }}
                >
                    Continue
                </Button>
            </DialogActions>
        </Dialog>
    );
}
